import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowRight, Building2, CheckCircle2, FileText, MessageCircle, Shield, Users } from 'lucide-react';
import ChatPopup from '../components/ChatPopup';
import { companyFormationProcessSteps } from '../mockData';

const CompanyFormation = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  const benefits = [
    'Limited liability protection',
    'Tax advantages',
    'Enhanced credibility',
    'Easier to raise capital',
    'Perpetual existence',
    'Professional structure'
  ];

  const entityTypes = [
    { icon: Building2, title: 'LLC Formation', desc: 'Flexible structure with liability protection and tax benefits' },
    { icon: Shield, title: 'Corporation', desc: 'Ideal for businesses seeking investment and formal structure' },
    { icon: Users, title: 'S Corporation', desc: 'Tax benefits with pass-through taxation for eligible businesses' },
    { icon: FileText, title: 'Nonprofit', desc: 'Tax-exempt organizations for charitable or social purposes' }
  ];

  // Using companyFormationProcessSteps from mockData

  const faqs = [
    {
      question: 'What business entity should I choose?',
      answer: 'The best entity depends on your business goals, tax situation, and liability concerns. We help you evaluate LLC, Corporation, S-Corp, and other options to find the right fit.'
    },
    {
      question: 'How long does company formation take?',
      answer: 'Formation typically takes 1-2 weeks depending on the state. We file your documents promptly and expedite processing when possible.'
    },
    {
      question: 'Do I need a registered agent?',
      answer: 'Yes, all businesses must have a registered agent in their state of formation. We provide this service for your convenience.'
    },
    {
      question: 'What ongoing requirements are there?',
      answer: 'Most entities require annual reports and maintaining good standing. We provide guidance and reminders to keep you compliant.'
    }
  ];

  return (
    <div className="min-h-screen">
      <ChatPopup isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-amber-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block mb-4">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto">
                <Building2 className="h-10 w-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Company Formation Services
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Start your business the right way with professional company formation services. We handle the paperwork so you can focus on building your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => setIsChatOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
                Form My Company <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" onClick={() => setIsChatOpen(true)} variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
                <MessageCircle className="mr-2 h-4 w-4" /> Talk to an Expert Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why Form a Company?</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-base">
                <p>
                  Forming a legal business entity provides crucial liability protection, tax advantages, and enhanced credibility. It separates your personal assets from business liabilities and creates a professional structure for growth.
                </p>
                <p>
                  Whether you're a solo entrepreneur or building a team, choosing the right business structure is one of the most important decisions you'll make. We guide you through the process and handle all the complexities.
                </p>
              </div>
            </div>
            <div className="relative h-[300px] lg:h-auto">
              <img 
                src="https://images.unsplash.com/photo-1573165850883-9b0e18c44bd2" 
                alt="Professional business woman working on company formation"
                className="rounded-2xl shadow-2xl w-full h-full max-h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Entity Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Entity Types We Form</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the structure that best fits your business
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {entityTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <Card key={index} className="border-2 border-gray-200 hover:border-orange-600 transition-colors">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{type.title}</h3>
                    <p className="text-gray-600">{type.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Key Benefits</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg">
                <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Formation Process</h2>
          </div>
          <div className="max-w-4xl mx-auto relative">
            {/* Connecting Line */}
            <div className="absolute left-[60px] top-[80px] bottom-[80px] w-1 bg-gradient-to-b from-orange-600 via-orange-500 to-amber-600"></div>
            
            <div className="space-y-12">
              {companyFormationProcessSteps.map((step) => (
                <div key={step.step} className="relative">
                  {/* Step Number Badge */}
                  <div className="absolute left-0 w-[120px] h-[120px]">
                    <div className="relative w-full h-full">
                      <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl rotate-6 opacity-20"></div>
                      <div className="relative bg-gradient-to-br from-orange-600 to-amber-600 rounded-2xl w-full h-full flex items-center justify-center shadow-xl">
                        <span className="text-4xl font-bold text-white">{step.step}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content Card */}
                  <div className="ml-[160px] bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-orange-100 hover:border-orange-300">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                        <p className="text-gray-600 text-base leading-relaxed">{step.description}</p>
                      </div>
                      <div className="ml-6 flex-shrink-0">
                        <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center">
                          <CheckCircle2 className="h-8 w-8 text-orange-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-gray-50 border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Building2 className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Form Your Company?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Let us handle the paperwork while you focus on your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setIsChatOpen(true)} className="bg-white text-orange-600 hover:bg-gray-100">
              Form My Company <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" onClick={() => setIsChatOpen(true)} variant="outline" className="border-white text-white hover:bg-white/10">
              <MessageCircle className="mr-2 h-4 w-4" /> Talk to an Expert Now
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanyFormation;
